import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { useCustomerAuth } from "@/lib/customerAuth";
import { toast } from "sonner";

export default function ReferPage() {
  const { customer, loading } = useCustomerAuth();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!customer) return;
    api.get("/customer/referrals").then((r) => setData(r.data)).catch(() => setErr(true));
  }, [customer]);

  const link = data ? `${window.location.origin}/?ref=${data.code}` : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Couldn't copy — long-press to copy manually"); }
  };
  const shareMsg = encodeURIComponent(`I turn unused gift cards into cash with GC SPARK — get a bonus when you use my link: ${link}`);

  return (
    <>
      <SEO title="Refer a friend & earn" description="Invite friends to GC SPARK. You both get a bonus when they sell their first gift card." />

      {/* Theme signup-hero split */}
      <section className="sign-up-section" data-testid="refer-hero" style={{ backgroundImage: "linear-gradient(135deg, #fff4e0 0%, #fde3f0 100%)" }}>
        <div className="sign-up-container">
          <div className="sign-up-text-container">
            <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#0b0d17", color: "#fff", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 22 }}>
              Refer & earn
            </div>
            <div className="sign-up-heading">
              <h1 className="h1 _700-bold">Give a bonus, <span style={{ color: "#dc4c2b" }}>get a bonus</span></h1>
            </div>
            <div className="sign-up-para">
              <p className="main-body-text _500-medium">
                Share your link. When a friend sells their first gift card, you both earn a cash bonus. Bonuses are reviewed to keep things fair.
              </p>
            </div>

            {/* Steps */}
            <ol style={{ listStyle: "none", padding: 0, marginTop: 34, display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                "Share your unique link with friends who have unused cards",
                "They sell their first card through your link",
                `Once approved, you both earn a $${data?.bonus_amount || 5} bonus`,
              ].map((t, i) => (
                <li key={t} style={{ display: "flex", gap: 14, alignItems: "flex-start", fontSize: 15.5, color: "#0b0d17", lineHeight: 1.5 }}>
                  <span style={{ flexShrink: 0, width: 32, height: 32, borderRadius: "50%", background: "#0b0d17", color: "#fff", fontWeight: 800, fontSize: 14, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
                  <span>{t}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="sign-up-form-div-wrapper">
            <div className="sign-up-form-wrapper" style={{ maxWidth: 460 }}>
              {loading ? (
                <div style={{ padding: 60, textAlign: "center", color: "#6c6376" }}>Loading…</div>
              ) : !customer ? (
                <div data-testid="refer-signin-gate" style={{ background: "#fff", padding: 36, borderRadius: 24, textAlign: "center", boxShadow: "0 30px 60px -30px rgba(41,45,85,.3)" }}>
                  <div style={{ width: 68, height: 68, margin: "0 auto 18px", borderRadius: 20, background: "linear-gradient(135deg, #dc4c2b, #f97316)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 30 }}>🎁</div>
                  <h3 className="h4 _700-bold">Sign in to get your link</h3>
                  <p style={{ color: "#6c6376", marginTop: 10, marginBottom: 22 }}>Your unique link and bonus tracker live in your account.</p>
                  <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                    <Link to="/signin?next=/refer" className="primary-button w-button" data-testid="refer-signin-btn">Sign in</Link>
                    <Link to="/signup?next=/refer" className="btn w-inline-block" data-testid="refer-signup-btn"><div className="btn-text">Create account</div></Link>
                  </div>
                </div>
              ) : err ? (
                <div style={{ background: "#fff", padding: 36, borderRadius: 24, textAlign: "center", color: "#6c6376" }}>Couldn't load referral details. Please refresh.</div>
              ) : data ? (
                <div data-testid="refer-share-card" style={{ background: "#fff", padding: 34, borderRadius: 24, boxShadow: "0 30px 60px -30px rgba(41,45,85,.3)" }}>
                  <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#ffe9dd", color: "#b4471f", fontSize: 12, fontWeight: 800, marginBottom: 16 }} data-testid="refer-bonus">
                    ${data.bonus_amount} for each of you
                  </div>
                  <h3 className="h4 _700-bold" style={{ marginBottom: 18 }}>Your referral link</h3>

                  <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                    <input value={link} readOnly data-testid="refer-link-input" onFocus={(e) => e.target.select()}
                      style={{ flex: "1 1 200px", padding: "12px 16px", borderRadius: 12, border: "1px solid #d8dcec", background: "#f8f9fc", fontSize: 13.5, fontWeight: 600, color: "#0b0d17", minWidth: 0 }} />
                    <button onClick={copy} data-testid="refer-copy-btn" className="primary-button w-button" style={{ border: "none", cursor: "pointer" }}>
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <div style={{ fontSize: 13, color: "#6c6376", marginBottom: 22 }}>Your code: <strong style={{ color: "#0b0d17", fontFamily: "monospace", letterSpacing: ".05em" }}>{data.code}</strong></div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <ShareBtn href={`https://wa.me/?text=${shareMsg}`} testid="refer-whatsapp">WhatsApp</ShareBtn>
                    <ShareBtn href={`mailto:?subject=${encodeURIComponent("Sell your gift cards for cash")}&body=${shareMsg}`} testid="refer-email">Email</ShareBtn>
                    <ShareBtn href={`https://twitter.com/intent/tweet?text=${shareMsg}`} testid="refer-twitter">X / Twitter</ShareBtn>
                  </div>

                  {/* Stats */}
                  <div data-testid="refer-stats" style={{ marginTop: 26, paddingTop: 24, borderTop: "1px solid #eaeaf0", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, textAlign: "center" }}>
                    <StatBlock v={data.referred_count} l="Referred" />
                    <StatBlock v={data.pending_count} l="Pending" />
                    <StatBlock v={`$${data.earned_total}`} l="Earned" />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function ShareBtn({ href, children, testid }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
      style={{ padding: "9px 16px", borderRadius: 90, border: "1px solid #d8dcec", color: "#0b0d17", fontWeight: 600, fontSize: 13.5, textDecoration: "none" }}>
      {children}
    </a>
  );
}
function StatBlock({ v, l }) {
  return (
    <div style={{ padding: "10px 6px", background: "#f8f9fc", borderRadius: 12 }}>
      <div style={{ fontFamily: '"Hanken Grotesk", sans-serif', fontSize: 22, fontWeight: 800, color: "#0b0d17" }}>{v}</div>
      <div style={{ fontSize: 11, color: "#6c6376", marginTop: 2 }}>{l}</div>
    </div>
  );
}
